import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const FEATURED_SERVICES = [
  {
    slug: 'kitchen-remodeling',
    title: 'Kitchen Remodeling',
    short: 'High-performance kitchens designed for daily living and entertaining.',
    label: 'Most Popular',
    image: 'https://public.readdy.ai/ai/img_res/4e153dda7788fb06df03346429041464.jpg',
  },
  {
    slug: 'bathroom-remodeling',
    title: 'Bathroom Remodeling',
    short: 'Spa-level bathrooms with luxury finishes and efficient layouts.',
    label: 'Top Rated',
    image: 'https://readdy.ai/api/search-image?query=Opulent%20luxury%20master%20bathroom%20renovation%20with%20sculptural%20freestanding%20white%20soaking%20tub%2C%20dramatic%20floor-to-ceiling%20large%20format%20marble%20slab%20walls%20with%20gold%20veining%2C%20frameless%20glass%20walk-in%20shower%20with%20brushed%20gold%20rain%20head%2C%20floating%20double%20vanity%20with%20concrete%20vessel%20sinks%2C%20backlit%20mirror%2C%20warm%20ambient%20lighting%2C%20serene%20spa%20sanctuary%2C%20architectural%20editorial%20photography%2C%20ultra%20premium%20residential%20Florida%20bathroom&width=900&height=500&seq=bcr-svc-bath-elite-v3&orientation=landscape',
  },
  {
    slug: 'full-home-renovations',
    title: 'Full Home Renovations',
    short: 'Complete interior transformations with cohesive design and precision.',
    label: 'Full Service',
    image: 'https://readdy.ai/api/search-image?query=Stunning%20open%20floor%20plan%20full%20home%20renovation%20interior%20with%20soaring%20coffered%2012-foot%20ceilings%2C%20flowing%20open%20living%20dining%20kitchen%20space%2C%20custom%20built-in%20millwork%20cabinetry%20throughout%2C%20dramatic%20natural%20stone%20accent%20fireplace%20wall%20as%20focal%20point%2C%20premium%20engineered%20herringbone%20hardwood%20floor%2C%20warm%20sophisticated%20ambient%20pendant%20lighting%2C%20luxury%20modern%20Florida%20interior%20architecture%2C%20wide%20angle%20cinematic%20photography&width=900&height=500&seq=bcr-svc-fullhome-elite-v3&orientation=landscape',
  },
  {
    slug: 'interior-upgrades',
    title: 'Interior Upgrades & Custom Work',
    short: 'Flooring, cabinetry, trim, and layout improvements that elevate every room.',
    label: 'Custom Work',
    image: 'https://readdy.ai/api/search-image?query=Exquisite%20custom%20built-in%20floor-to-ceiling%20dark%20walnut%20library%20cabinetry%20with%20integrated%20display%20shelves%2C%20glass-front%20display%20cabinets%2C%20rolling%20brass%20library%20ladder%2C%20warm%20directed%20accent%20lighting%20highlighting%20rich%20wood%20grain%2C%20elegant%20crown%20molding%20and%20architectural%20millwork%20details%2C%20luxury%20home%20study%20interior%2C%20sophisticated%20dramatic%20warm%20photography%2C%20premium%20Florida%20residential%20craftsmanship&width=900&height=500&seq=bcr-svc-interior-elite-v3&orientation=landscape',
  },
];

interface ServicesOverviewProps {
  className?: string;
  limit?: number;
}

export default function ServicesOverview({ className = '', limit }: ServicesOverviewProps) {
  const { ref, visible } = useScrollAnimation();
  const services = limit ? FEATURED_SERVICES.slice(0, limit) : FEATURED_SERVICES;

  return (
    <section className={`bg-white py-32 px-6 lg:px-16 xl:px-24 ${className}`}>
      <div className="max-w-6xl mx-auto">

        {/* Heading row */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4">Our Services</p>
            <h2 className="font-serif text-4xl lg:text-[52px] text-stone-900 leading-tight max-w-2xl" style={{ letterSpacing: '-0.02em' }}>
              Remodeling Services Built Around Quality, Function &amp; Design
            </h2>
          </div>
          <Link
            to="/services"
            className="hidden lg:inline-flex items-center gap-2 text-stone-500 hover:text-accent font-medium text-sm whitespace-nowrap transition-colors group"
          >
            Explore All Services
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        </div>

        {/* Editorial grid */}
        <div
          ref={ref}
          className={`grid grid-cols-1 md:grid-cols-12 gap-3 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          data-product-shop
        >
          {/* Featured large card — spans 7 cols */}
          <Link
            to={`/services#${services[0].slug}`}
            className="group relative md:col-span-7 overflow-hidden rounded-2xl block"
          >
            <div className="relative h-[480px] md:h-[560px] overflow-hidden">
              <img
                src={services[0].image}
                alt={services[0].title}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/30 to-transparent" />
              <div className="absolute top-5 left-5">
                <span className="bg-accent text-white text-xs font-semibold px-3.5 py-1.5 rounded-full tracking-wide">{services[0].label}</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <h3 className="font-serif text-2xl lg:text-3xl text-white mb-2 group-hover:text-accent/90 transition-colors">{services[0].title}</h3>
                <p className="text-white/55 text-sm leading-relaxed mb-5">{services[0].short}</p>
                <div className="flex items-center gap-2 text-accent text-sm font-semibold">
                  <span>Explore Service</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </div>
          </Link>

          {/* Right column — stacked 2 cards spanning 5 cols */}
          <div className="md:col-span-5 flex flex-col gap-3">
            {services.slice(1, 3).map((service) => (
              <Link
                key={service.slug}
                to={`/services#${service.slug}`}
                className="group relative overflow-hidden rounded-2xl block flex-1"
              >
                <div className="relative h-[200px] sm:h-[265px] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/20 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/12 backdrop-blur-sm border border-white/15 text-white text-xs font-medium px-3 py-1 rounded-full">{service.label}</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-serif text-lg text-white mb-1 group-hover:text-accent/90 transition-colors">{service.title}</h3>
                    <p className="text-white/50 text-xs leading-relaxed">{service.short}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Bottom wide card — spans 12 cols, 1/3 height */}
          {services[3] && (
            <Link
              to={`/services/${services[3].slug}`}
              className="group relative md:col-span-12 overflow-hidden rounded-2xl block"
            >
              <div className="relative h-[200px] overflow-hidden">
                <img
                  src={services[3].image}
                  alt={services[3].title}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-950/50 to-transparent" />
                <div className="absolute top-5 left-5 flex items-center gap-3">
                  <span className="bg-white/12 backdrop-blur-sm border border-white/15 text-white text-xs font-medium px-3 py-1 rounded-full">{services[3].label}</span>
                </div>
                <div className="absolute inset-0 flex items-center px-7 lg:px-10">
                  <div>
                    <h3 className="font-serif text-xl lg:text-2xl text-white mb-1.5 group-hover:text-accent/90 transition-colors">{services[3].title}</h3>
                    <p className="text-white/50 text-sm max-w-md leading-relaxed">{services[3].short}</p>
                  </div>
                  <div className="ml-auto hidden lg:flex items-center gap-2 text-accent font-semibold text-sm">
                    Explore
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* Mobile CTA */}
        <div className="lg:hidden mt-8 text-center">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 border border-stone-200 hover:border-accent text-stone-700 hover:text-accent font-medium px-7 py-3.5 rounded-lg transition-colors whitespace-nowrap text-sm"
          >
            Explore All Remodeling Services
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs" />
            </div>
          </Link>
        </div>

        {/* Mid-page CTA after services */}
        <div className="mt-12 bg-[#F9F7F3] border border-stone-100 rounded-2xl p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <p className="font-serif text-2xl text-stone-900 mb-1">Ready to start your project in Brandon, FL?</p>
            <p className="text-stone-500 text-sm">Free in-home consultation · Licensed &amp; insured · No obligation</p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-shrink-0">
            <a
              href="tel:+18133620114"
              className="inline-flex items-center gap-2 text-stone-700 hover:text-accent border border-stone-200 hover:border-accent/40 px-5 py-3 rounded-lg transition-colors whitespace-nowrap text-sm font-medium"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-accent" />
              </div>
              813-362-0114
            </a>
            <Link
              to="/about"
              className="inline-flex items-center gap-3 bg-stone-950 hover:bg-stone-800 text-white font-semibold px-7 py-3 rounded-lg transition-colors whitespace-nowrap group text-sm"
            >
              Request a Consultation
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1 text-xs" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
